import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Field, FieldProps, Form } from 'formik';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Page,
  Button,
  Input,
  Loading,
  useToast,
  FieldWrapper,
  ErrorComponent,
  ButtonWidth,
  Notification,
  FormWrapper,
  MLPVersion,
  ButtonStyles,
  FAIcon,
  IconStyle,
} from '../../components';
import { useAPICall, useAppDispatch, useAppSelector } from '../../../hooks';
import { fetchLoginUser } from '../../../sdk/store/authActions';
import AuthService from '../../../sdk/services/authService';
import { LoginSchema } from '../../../sdk/validations/loginSchema';
import type { AccountDataModel, DynamicObjectInterface, LoginModel } from '../../../sdk/type';
import personalizeImg from '../../../assets/image/personalize.png';
import { LoggerService, getState, updateState } from '../../utils';
import styles from './Auth.module.scss';

export const Login = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const apiCall = useAPICall();

  const { userInfo, token, loginError, errorMessage } = useAppSelector(state => state.auth);

  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const teamParam = searchParams.get('t');
  const idParam = searchParams.get('id');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loginId, setLoginId] = useState('');
  const [step, setStep] = useState(1);
  const [accAbo, setAccAbo] = useState<AccountDataModel[]>([]);
  const [accSelected, setAccSelected] = useState('');
  const [isImpersonate, setIsImpersonate] = useState(false);

  useEffect(() => {
    (async () => {
      let fromImpersonate = await getState('fromImpersonate');
      setIsImpersonate(fromImpersonate || false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (typeof window !== 'undefined') {
        if (userInfo && token !== 'INVALID' && token !== '') {
          LoggerService.log("Login: update state..")
          if (source && teamParam) {
            await updateState(['source', source], ['team', teamParam]);
          }else if (idParam){
            await updateState(['id', idParam]);
          }
          window.location.href = '/';
        } else {
          setIsLoading(false);
        }
      }
    })();
  }, [userInfo, token, loginError, errorMessage]);

  const doLoginBtn = async (values: LoginModel) => {
    setIsLoading(true);
    const { amwayId, name, password, partyId } = values;
    if (amwayId && name && password) {
      const res: DynamicObjectInterface = await apiCall(AuthService.doLogin, {
        amwayId,
        name,
        password,
        partyId,
      });
      dispatch(fetchLoginUser(res.data?.token));
    } else {
      addToast({
        id: 'login_failed',
        content: <Notification message={t('auth.errors.noUsernamePassword')} />,
        topPosition: '20px',
      });
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const previousStep = () => {
    setStep(step - 1);
  };

  const getUserAccount = async () => {
    const account = await apiCall(AuthService.getUserAccountAdmin, loginId);
    if (account.status !== 200) {
      LoggerService.log('Error: Unable to fetch data');
      return;
    }
    if (account.data?.data?.length === 0) {
      LoggerService.log('No user register under this Abo Number.');
      return;
    }
    setAccAbo(account.data.data);
    nextStep();
  };

  const handleBackToAdmin = async () => {
    await updateState(['fromImpersonate', false]);
  };

  const initialValues: LoginModel = {
    amwayId: '',
    password: '',
    name: '',
    partyId: '',
  };

  return (
    <Page className={styles['login']}>
      <FormWrapper className={styles['login-container']} caption={t('auth.login')} style={{ margin: '100px auto' }}>
        {isImpersonate && (
          <Link
            to={'/admin'}
            onClick={() => handleBackToAdmin()}
            className={styles['login-container-exit-impersonate']}
            title={t('framework.menu.exitImpersonate')}
          >
            <FAIcon
              iconName="arrow-right-to-bracket"
              iconStyle={IconStyle.SOLID}
              className={`${styles['icon']} fa-lg`}
            />
          </Link>
        )}
        <Formik
          initialValues={initialValues}
          onSubmit={doLoginBtn}
          validationSchema={LoginSchema}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({ values, handleChange, handleBlur, isValid, setFieldValue, errors }) => {
            const nextButton = isValid && values.amwayId && values.name && values.password;
            const handleAmwayIdChange = (e: DynamicObjectInterface) => {
              setLoginId(e.target.value);
              handleChange(e);
            };
            const handleAccountSelect = (name: string, partyId: string) => {
              setAccSelected(name);
              setFieldValue('name', name);
              setFieldValue('partyId', partyId);
            };

            return (
              <Form>
                {step === 1 && (
                  <>
                    <FieldWrapper className={styles['login-field-container']}>
                      <Field type="text" name="amwayId" validationContext={{ isNew: true }}>
                        {({ meta }: FieldProps) => (
                          <>
                            <Input
                              label={t('auth.amwayId')}
                              id="amwayId"
                              name="amwayId"
                              value={values?.amwayId}
                              onChange={handleAmwayIdChange}
                              onBlur={handleBlur}
                              mlpVersion={MLPVersion.ONE}
                            />
                            <ErrorComponent touched={meta.touched} error={meta.error} />
                          </>
                        )}
                      </Field>
                    </FieldWrapper>
                    <div className={styles['login-btn-layout']}>
                      <Button
                        type="button"
                        title={t('auth.next')}
                        disabled={!loginId}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                        onClick={getUserAccount}
                      />
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className={styles['login-field-container']}>
                      <p>Select user account under this Amway ID</p>
                      <FieldWrapper>
                        <div className={styles['login-account']}>
                          {accAbo.map(acc => (
                            <button
                              type="button"
                              onClick={() => handleAccountSelect(acc.name, acc.partyId)}
                              className={`${styles['login-account-container']} ${
                                accSelected === acc.name ? styles['login-account-container-selected'] : ''
                              }`}
                            >
                              <img src={personalizeImg} alt="personalize" className={styles['login-account-img']} />
                              {acc.name}
                            </button>
                          ))}
                        </div>
                      </FieldWrapper>
                    </div>

                    <div className={styles['login-btn-layout']}>
                      <Button
                        type="button"
                        title={t('auth.previous')}
                        buttonStyle={ButtonStyles.OUTLINE}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                        onClick={() => {
                          setAccSelected('');
                          previousStep();
                        }}
                      />
                      <Button
                        type="button"
                        title={t('auth.next')}
                        disabled={!accSelected}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                        onClick={nextStep}
                      />
                    </div>
                  </>
                )}

                {step === 3 && (
                  <>
                    <div className={styles['login-field-container']}>
                      <FieldWrapper>
                        <Input
                          label={t('auth.amwayId')}
                          id="amwayId"
                          name="amwayId"
                          value={loginId}
                          disabled
                          mlpVersion={MLPVersion.ONE}
                        />
                      </FieldWrapper>

                      <FieldWrapper>
                        <div className={styles['login-account']}>
                          <div
                            className={`${styles['login-account-container']} ${styles['login-account-container-display']}`}
                          >
                            <img src={personalizeImg} alt="personalize" className={styles['login-account-img']} />
                            {accSelected}
                          </div>
                        </div>
                      </FieldWrapper>

                      <FieldWrapper>
                        <Field type="password" name="password">
                          {({ meta }: FieldProps) => (
                            <>
                              <Input
                                label={t('auth.password')}
                                id="password"
                                name="password"
                                type={isPasswordVisible ? 'text' : 'password'}
                                value={values?.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                mlpVersion={MLPVersion.ONE}
                                inputIcon={{ iconName: isPasswordVisible ? 'eye-slash' : 'eye' }}
                                onToggleVisibility={() => {
                                  setIsPasswordVisible(!isPasswordVisible);
                                }}
                              />
                              <ErrorComponent touched={meta.touched} error={meta.error} />
                            </>
                          )}
                        </Field>
                      </FieldWrapper>
                    </div>

                    <div className={styles['login-btn-layout']}>
                      <Button
                        type="button"
                        title={t('auth.previous')}
                        buttonStyle={ButtonStyles.OUTLINE}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                        onClick={() => {
                          setAccSelected('');
                          previousStep();
                        }}
                      />
                      <Button
                        type="submit"
                        title={t('auth.loginBtn')}
                        disabled={!nextButton}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                      />
                    </div>
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </FormWrapper>
      {isLoading && <Loading />}
    </Page>
  );
};
